import { useEffect, useRef, useState, type ComponentType } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  FileUp,
  Flag,
  NotebookPen,
  GraduationCap,
  Landmark,
  MapPin,
  Plus,
  ReceiptText,
  Siren,
  Target,
  UserPlus,
  Wrench,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import type { AuthUser } from '@/types';

interface Action {
  label: string;
  icon: ComponentType<{ className?: string }>;
  to: string;
  /** Who may use it. The API refuses everyone else anyway; this keeps the menu honest. */
  allowed: (user: AuthUser) => boolean;
}

const MANAGERS = ['super_admin', 'user_admin', 'developer'];
const PROJECT_RUNNERS = [...MANAGERS, 'project_admin'];
const SITE_STAFF = [...PROJECT_RUNNERS, 'site_admin'];

const has = (roles: string[]) => (user: AuthUser) => roles.includes(user.role) || user.is_client;

/** Grouped the way the day goes: the site first, then the business, then the office. */
const GROUPS: { label: string; actions: Action[] }[] = [
  {
    label: 'Site',
    actions: [
      { label: 'New Site Diary', icon: NotebookPen, to: '/site-diaries', allowed: has(SITE_STAFF) },
      { label: 'New Defect', icon: Flag, to: '/defects?new=1', allowed: has(SITE_STAFF) },
      { label: 'New Emergency roll-call', icon: Siren, to: '/site-management', allowed: has(SITE_STAFF) },
      { label: 'Upload compliance document', icon: FileUp, to: '/compliance?upload=1', allowed: has(SITE_STAFF) },
      { label: 'New Induction', icon: GraduationCap, to: '/safety/inductions/new', allowed: has(SITE_STAFF) },
      { label: 'New Person', icon: UserPlus, to: '/people/new', allowed: has(SITE_STAFF) },
    ],
  },
  {
    label: 'Business',
    actions: [
      { label: 'New Project', icon: MapPin, to: '/projects/new', allowed: has(PROJECT_RUNNERS) },
      { label: 'New Prospect', icon: Target, to: '/prospects/new', allowed: has(PROJECT_RUNNERS) },
      { label: 'New Tender', icon: Landmark, to: '/pre-construction/tenders/new', allowed: has(PROJECT_RUNNERS) },
    ],
  },
  {
    label: 'Office',
    actions: [
      { label: 'New Tool', icon: Wrench, to: '/warehouse/tools/new', allowed: has(SITE_STAFF) },
      { label: 'New Purchase order', icon: ReceiptText, to: '/finance/purchase-orders/new', allowed: (user) => user.finance_access },
    ],
  },
];

/** The topbar's "New" — start any record from anywhere, BuildPass-style. */
export function NewMenu({ user }: { user: AuthUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const groups = GROUPS.map((group) => ({ ...group, actions: group.actions.filter((action) => action.allowed(user)) })).filter((group) => group.actions.length > 0);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  useEffect(() => setOpen(false), [location.pathname, location.search]);

  if (groups.length === 0) return null;

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="New"
        className={cn(
          'flex h-10 items-center gap-1.5 rounded-full px-3 text-sm font-semibold transition-colors',
          open ? 'bg-signal-50 text-signal-700' : 'bg-cloud text-body hover:bg-signal-50 hover:text-signal-700',
        )}
      >
        <Plus aria-hidden className="h-[18px] w-[18px]" />
        <span className="hidden xl:inline">New</span>
        <ChevronDown aria-hidden className="hidden h-4 w-4 text-muted xl:block" />
      </button>

      {open && (
        <div role="menu" className="absolute right-0 top-full z-30 mt-2 w-64 animate-fade-in overflow-hidden rounded-xl border border-line bg-white p-1.5 shadow-raised">
          {groups.map((group, index) => (
            <div key={group.label} className={cn(index > 0 && 'mt-1 border-t border-line pt-1')}>
              <p className="px-3 pb-1 pt-1.5 text-2xs font-semibold uppercase tracking-wide text-muted">{group.label}</p>
              {group.actions.map((action) => (
                <button
                  key={action.label}
                  type="button"
                  role="menuitem"
                  onClick={() => navigate(action.to)}
                  className="flex w-full items-center gap-2.5 rounded px-3 py-2 text-sm text-body transition-colors hover:bg-cloud"
                >
                  <action.icon aria-hidden className="h-4 w-4 text-muted" />
                  {action.label}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

